import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Traps Tab/Shift+Tab focus inside the element `ref` points to while `active`
// is true, moves focus onto its first focusable child on open, restores
// focus to whatever was focused before the dialog opened on close, and calls
// `onClose` (if given) when Escape is pressed. Attach `ref` to the dialog's
// outermost panel — the one that actually contains the buttons/inputs, not a
// full-screen backdrop wrapper.
export default function useFocusTrap(ref, active, onClose) {
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    previouslyFocused.current = document.activeElement;

    const focusables = () =>
      Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null
      );

    const first = focusables()[0];
    if (first) first.focus({ preventScroll: true });

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        if (onClose) {
          e.stopPropagation();
          onClose();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    node.addEventListener('keydown', handleKeyDown);
    return () => {
      node.removeEventListener('keydown', handleKeyDown);
      const toRestore = previouslyFocused.current;
      if (toRestore && typeof toRestore.focus === 'function' && document.contains(toRestore)) {
        toRestore.focus({ preventScroll: true });
      }
    };
  }, [active, ref, onClose]);
}
